import React from 'react';
import classNames from 'classnames/bind';
import { Skeleton } from '@mantine/core';
import { useForm } from 'react-hook-form';

import { Tag } from 'types/tag';

import { downloadFile } from 'services/download_file';
import { useAddEntityTags } from 'tags/hooks/useAddEntityTag';
import { useCamera } from 'cameras/hooks/useCamera';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteEntityTags } from 'tags/hooks/useDeleteEntityTags';
import { FileSearchMetadata } from 'files/components/FileSearchMetadata';
import { useFileWithSearchMetadata } from 'files/hooks/useFileWithSearchMetadata';
import { ImageSearchType } from 'files/hooks/useFilesSearch';
import { useModal } from 'hooks/useModal';
import { usePipeline } from 'pipelines/hooks/usePipeline';
import { useSpringState } from 'hooks/useSpringState';
import { useStream } from 'streams/hooks/useStream';
import { useUpdateFile } from 'files/hooks/useUpdateFile';
import { useTrackableNames } from 'trackables/hooks';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { EntityList, EntityListItem } from 'components/EntityList';
import { Field } from 'components/Field/Field';
import { FileDetailPreview } from 'files/views/FileDetail/Preview';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { ImageSearchDialog } from 'files/components/ImageSearchDialog/ImageSearchDialog';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Tabs, TabsButton, TabsContent, TabsList } from 'components/Tabs/Tabs';
import { TagsList } from 'tags/components/TagsList/TagsList';
import { Textarea } from 'components/Textarea/Textarea';

import styles from './FilesSearchDetail.module.scss';

const c = classNames.bind(styles);

type FileFieldValues = { description: string; is_retained: boolean };

type FilesSearchDetailProps = {
  id: string;
  onImageSearch?: (embedding: number[], type: ImageSearchType, sourceFile: File) => void;
};

export function FilesSearchDetail({ id, onImageSearch }: FilesSearchDetailProps) {
  const { close } = useModal();

  const { data: currentPlan } = useCurrentPlan();

  const { data: file, isLoading: isLoadingFile } = useFileWithSearchMetadata(
    id,
    {
      params: { include_search_metadata: currentPlan?.can_search },
    }
  );

  const { data: stream, isInitialLoading: isLoadingStream } = useStream(
    file?.stream_id
  );
  const { data: camera, isInitialLoading: isLoadingCamera } = useCamera(
    stream?.camera_id
  );
  const { data: pipeline, isInitialLoading: isLoadingPipeline } = usePipeline(
    file?.pipeline_id
  );

  const trackableIds = React.useMemo(
    () => (Array.isArray(file?.trackables) ? file.trackables : []),
    [file?.trackables]
  );
  const trackableNamesQuery = useTrackableNames(trackableIds);

  const {
    formState: { dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<FileFieldValues>();

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const [isSuccess, setIsSuccess] = useSpringState(false, 2000);

  const { mutate: mutateFile, isLoading: isUpdating } = useUpdateFile({
    onSuccess({ description, is_retained }) {
      setIsSuccess(true);
      reset({ description, is_retained });
    },
  });

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const [isImageSearchOpen, setIsImageSearchOpen] = React.useState(false);
  const [capturedFrame, setCapturedFrame] = React.useState<File | null>(null);

  const isVideo = file?.mime_type?.includes('video');
  const isImage = file?.mime_type?.includes('image');

  function captureFromElement(
    source: HTMLVideoElement | HTMLImageElement,
    width: number,
    height: number,
    filename: string
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(source, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const frameFile = new File([blob], filename, { type: 'image/png' });
      setCapturedFrame(frameFile);
      setIsImageSearchOpen(true);
    }, 'image/png');
  }

  function handleCaptureVideoFrame() {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    captureFromElement(video, video.videoWidth, video.videoHeight, 'video-frame.png');
  }

  function handleCaptureImage() {
    const img = imageRef.current;
    if (!img) return;

    captureFromElement(img, img.naturalWidth, img.naturalHeight, 'image-capture.png');
  }

  function handleImageSearchSelect(embedding: number[], type: ImageSearchType, sourceFile: File) {
    setIsImageSearchOpen(false);
    setCapturedFrame(null);
    onImageSearch?.(embedding, type, sourceFile);
  }

  const { mutate: addTags } = useAddEntityTags({
    entity: 'files',
    listQueryKey: ['files'],
    singleQueryKey: ['file'],
  });

  const { mutate: removeTags } = useDeleteEntityTags({
    entity: 'files',
    listQueryKey: ['files'],
    singleQueryKey: ['file'],
  });

  function handleAddTags(tag: Tag) {
    if (!file) {
      return;
    }

    addTags({ id: file.id, tagIds: [tag.id] });
  }

  function handleRemoveTags(tagId: Tag['id']) {
    if (!file) {
      return;
    }

    removeTags({ id: file.id, tagIds: [tagId] });
  }

  function onSubmit({ description, is_retained }: FileFieldValues) {
    if (!file) {
      return;
    }

    mutateFile({
      id: file.id,
      description: dirtyFields.description ? description : undefined,
      is_retained: dirtyFields.is_retained ? is_retained : undefined,
    });
  }

  return (
    <div className={c('file-detail')}>
      <div className={c('header')}>
        {isLoadingFile ? (
          <Skeleton height="var(--size-lg)" width="80%" />
        ) : (
          <Heading level="1" className={c('heading')}>
            {file?.name}
          </Heading>
        )}

        {stream && (
          <EntityList className={c('entities')}>
            <EntityListItem
              icon="stream"
              label="Stream"
              type="streams"
              loading={isLoadingStream}
              entity={stream}
              onLinkClick={() => close()}
            />
            <EntityListItem
              icon="camera"
              type="cameras"
              label="Camera"
              loading={isLoadingCamera}
              entity={camera}
              onLinkClick={() => close()}
            />
            <EntityListItem
              icon="pipeline"
              type="pipelines"
              label="Workflow"
              loading={isLoadingPipeline}
              entity={pipeline}
              onLinkClick={() => close()}
            />
          </EntityList>
        )}

        <IconButton
          className={c('button')}
          icon="download"
          label="Download"
          size="small"
          variant="secondary"
          loading={isLoadingFile}
          onClick={() =>
            file && file.data_url && downloadFile(file.name, file.data_url)
          }
          disabled={!file?.data_url}
          showLabel
        />

        {file && (
          <TagsList
            defaultValue={file.tags}
            className={c('tags')}
            onAdd={handleAddTags}
            onRemove={handleRemoveTags}
          />
        )}
      </div>
      <Tabs defaultValue="preview">
        <TabsList>
          <TabsButton value="preview">Preview</TabsButton>
        </TabsList>

        <TabsContent value="preview" className={c('preview')}>
          <FileDetailPreview
            url={file?.data_url}
            mimeType={file?.mime_type}
            cloudStatus={file?.cloud_status}
            isLoading={isLoadingFile}
            onVideoRef={(el) => { videoRef.current = el; }}
            onImageRef={(el) => { imageRef.current = el; }}
          />

          {(isVideo || isImage) && onImageSearch && (
            <Button
              variant="secondary"
              size="small"
              onClick={isVideo ? handleCaptureVideoFrame : handleCaptureImage}
              disabled={isLoadingFile || !file?.data_url}
              className="mt-2"
            >
              <Icon name="image" className="!mr-1" />
              <span>Appearance search</span>
            </Button>
          )}

          {file &&
          (file.objects?.length ||
            file.events?.length ||
            file.texts?.length ||
            file.trackables?.length) ? (
            <FileSearchMetadata
              className={c('search-metadata')}
              layout="detail"
              objects={file.objects}
              events={file.events}
              texts={file.texts}
              trackables={trackableIds}
              trackableItemsLookup={trackableNamesQuery.data?.items}
              trackableListsLookup={trackableNamesQuery.data?.lists}
            />
          ) : null}

          <form className={c('form')} onSubmit={handleSubmit(onSubmit)}>
            <Field label="Description">
              {file ? (
                <Textarea
                  {...register('description')}
                  defaultValue={file.description}
                />
              ) : (
                <Skeleton width="100%" height="6.25rem" />
              )}
            </Field>

            {currentPlan?.can_retain_recordings && (
              <Field
                label="Retain"
                // FIXME Mention retention days
                info="Enable to prevent this recording from being deleted after retention period."
              >
                <div className={c('retain')}>
                  {file ? (
                    <LightSwitch
                      {...register('is_retained')}
                      defaultChecked={file.is_retained}
                    />
                  ) : (
                    <Skeleton width="2.5rem" height="1.5rem" radius="6rem" />
                  )}
                </div>
              </Field>
            )}

            <ButtonGroup>
              <Button
                variant="primary"
                type="submit"
                loading={isUpdating}
                disabled={!isDirty || !file}
              >
                Save settings
              </Button>

              {isDirty && (
                <InlineNotification>Unsaved changes</InlineNotification>
              )}

              {isSuccess && (
                <InlineNotification intent="success">Saved!</InlineNotification>
              )}
            </ButtonGroup>
          </form>
        </TabsContent>
      </Tabs>

      {isImageSearchOpen && (
        <ImageSearchDialog
          open={isImageSearchOpen}
          onClose={() => {
            setIsImageSearchOpen(false);
            setCapturedFrame(null);
          }}
          onSelect={handleImageSearchSelect}
          initialFile={capturedFrame}
        />
      )}
    </div>
  );
}
